import React from 'react'
import { Theats } from '../../../shared/types/training.type'
import I18nDataGrid from '../../global/I18nDataGrid'
import { GridColDef, GridRenderCellParams, GridRowClassNameParams, GridValueGetterParams } from '@mui/x-data-grid';
import { t } from 'i18next';
import dayjs from 'dayjs';
import Duration from 'dayjs/plugin/duration'
import { convertStrideLength, formatMetricIntoImperialDistance, getSpeedForChart, getSpeedTooltipForChart } from '../../../shared/utils/conversion';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { toNumber } from 'lodash';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../../../hooks/useAuth';
function HeatGrid({ heats, speedUnit, onRowHover, hoveredHeat, printable = false }: {
    printable?: boolean,
    heats?: Theats[], speedUnit: string, onRowHover: (row?: Theats, action?: 'entered' | 'leave') => void, hoveredHeat: number | null
}) {
    const metric = useSelector<RootState>(state => state.user.user?.unit_system) as "metric" | "imperial" | undefined
    const imperial_distance_miles_only = useSelector<RootState>(state => state.user.user?.imperial_distance_miles_only) as boolean
    const { getSpeedUnit, getMetric } = useAuth()
    const columns: GridColDef[] = [
        {
            field: 'idx',
            renderHeader: () => { return "" },
            renderCell: (params: GridRenderCellParams<Theats>) =>
                params.api.getRowIndexRelativeToVisibleRows(params.row.id) + 1,

        },
        {
            field: 'time',
            renderHeader: () => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} textAlign={'center'} justifyContent={'center'}>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('intervals:time')}</Typography>
                    <Typography variant='caption'>({t('intervals:min')})</Typography>
                </Box>
            ), ...(printable ? { width: 100 } : { flex: 1 }),

            valueGetter: (val: GridValueGetterParams<Theats>) => {
                return dayjs.duration(val.row.duration, 'seconds').format('mm:ss')
            }
        },
        {
            field: 'distance',
            renderHeader: () => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} textAlign={'center'} justifyContent={'center'}>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('intervals:distance')}</Typography>
                    <Typography variant='caption'>({metric === 'metric' ? t('intervals:meters') : t('intervals:furlongs')})</Typography>
                </Box>
            ),
            ...(printable ? { width: 100 } : { flex: 1 }),
            valueGetter: (val: GridValueGetterParams<Theats>) => {
                return metric === 'metric' ? Math.round(val.row.distance * 5) : formatMetricIntoImperialDistance(val.row.distance * 5, imperial_distance_miles_only)
            }

        },
        {
            field: 'pace',
            renderHeader: () => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} textAlign={'center'} justifyContent={'center'}>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('intervals:pace')}</Typography>
                    <Typography variant='caption'>({t(getSpeedUnit()?.key ?? '')})</Typography>
                </Box>
            ),
            ...(printable ? { width: 100 } : { flex: 1 }),
            valueGetter: (val: GridValueGetterParams<Theats>) => {
                return getSpeedTooltipForChart(
                    toNumber(
                        getSpeedForChart(toNumber(val.row.mean_speed), getSpeedUnit()?.value ?? "")
                    ),
                    getSpeedUnit()?.value ?? '', false)
            }

        },

        {
            field: 'mean_hr',
            renderHeader: () => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} textAlign={'center'} justifyContent={'center'}>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('intervals:averageFC')}</Typography>
                    <Typography variant='caption'>({t('intervals:bpm')})</Typography>
                </Box>
            ),
            ...(printable ? { width: 100 } : { flex: 1 }),

        },
        {
            field: 'max_hr',
            renderHeader: () => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} textAlign={'center'} justifyContent={'center'}>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('intervals:maxHeartRate')}</Typography>
                    <Typography variant='caption'>({t('intervals:bpm')})</Typography>
                </Box>
            ),

            flex: 1

        },
        {
            field: 'min_hr',
            renderHeader: () => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} textAlign={'center'} justifyContent={'center'}>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('intervals:minFC')}</Typography>
                    <Typography variant='caption'>({t('intervals:bpm')})</Typography>
                </Box>
            ),
            flex: 1

        },
        {
            field: 'mean_stride_frequency',
            renderHeader: () => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} textAlign={'center'} justifyContent={'center'}>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('intervals:averageCad')}</Typography>
                    <Typography variant='caption'>({t('intervals:stepPerSecond')})</Typography>
                </Box>
            ),
            flex: 1,
            valueGetter: (val: GridValueGetterParams<Theats>) => {
                return (val.row.mean_stride_frequency / 50).toFixed(2)
            }

        },
        {
            field: 'mean_stride_length',
            renderHeader: () => (
                <Box display={'flex'} flexDirection={'column'} alignItems={'center'} textAlign={'center'} justifyContent={'center'}>
                    <Typography sx={{ fontSize: { md: 14, xs: 12 } }}>{t('intervals:averageAmpl')}</Typography>
                    <Typography variant='caption'>({getMetric()})</Typography>
                </Box>
            ),
            valueGetter: (val: GridValueGetterParams<Theats>) => {
                return convertStrideLength(metric, val.row.mean_stride_length) ?? '-'
            },
            flex: 1

        },
    ];
    const handlePopperOpen = (event: any) => {
        if (event) {
            let id = event.currentTarget?.dataset?.id
            const row = heats && heats.find((r) => r.id === toNumber(id));
            onRowHover(row, 'entered')
        }

    };

    const handlePopperClose = (event: any) => {
        if (event) {

            let id = event.currentTarget?.dataset?.id
            const row = heats && heats.find((r) => r.id === toNumber(id));

            onRowHover(row, 'leave')
        }
    }

    return (
        <I18nDataGrid
            columns={columns}
            rows={heats ?? []}
            sx={{
                "& .MuiDataGrid-row:hover": {
                    backgroundColor: 'rgba(255, 255, 255, 0.5)'
                }
            }}
            getRowClassName={(param: GridRowClassNameParams<Theats>) => param.row.id === hoveredHeat ? 'hovered-row' : 'interval_row'}
            componentsProps={{
                row: {
                    onMouseOver: handlePopperOpen,
                    onMouseLeave: handlePopperClose
                }
            }}
        />


    )
}

export default HeatGrid